import pluralize from 'pluralize'
import { useEffect, useRef } from 'react'
import toast from 'react-hot-toast'

import {
  FvButton,
  FvLinkButton,
  Icon,
  TextAreaField,
  ValidatedForm,
} from '@fv/client-components'
import { isMexicoIntra, modelToDto } from '@fv/client-core'
import { MEXICAN_TAX_CHARGES } from '@fv/models'

import { supportMessage } from '@/constants'
import { getBidExpiration } from '@/utils/getBidExpiration'

import { useSendMessage } from '../messaging/queries'
import { useQuotes } from '../opp-card/hooks/useQuotes'
import { useRetractQuote } from '../opp-card/hooks/useRetractQuote'
import { useSubmitQuotes } from '../opp-card/hooks/useSubmitQuotes'
import { QuoteFormEntry } from './QuoteFormEntry'
import {
  useQuotesFormActions,
  useQuotesFormHelpers,
  useQuotesFormState,
} from './QuotesFormProvider'
import { QuotingDescription } from './QuotingDescription'
import { hasQuoteChanged, quoteTerm, useCanEditQuotes } from './utils'

type Props = {
  hasQuoted?: boolean
}

export const QuotesForm = ({ hasQuoted }: Props) => {
  const formRef = useRef<HTMLFormElement>(null)
  const { models, message, selected, opportunity } = useQuotesFormState()
  const { loadId, quoteRequestId, status, locations, fuel } = opportunity
  const term = quoteTerm(status)
  const { switchToCharges } = useQuotesFormHelpers()
  const { setMessage, setContractedRateLock } = useQuotesFormActions()
  const quotesQuery = useQuotes(loadId)
  const quotes = quotesQuery.data ?? []
  const { isExpired } = getBidExpiration(opportunity)
  const canEdit = useCanEditQuotes(opportunity, quotes)
  const { sendMessage, isSendingMessage } = useSendMessage(loadId)
  const retractQuote = useRetractQuote(quoteRequestId)
  const submitQuotes = useSubmitQuotes({ loadId, quoteRequestId })
  const isBusy =
    retractQuote.isPending || submitQuotes.isPending || isSendingMessage
  const hasChargeBreakdown = models[0]?.charges?.length > 1
  const mexicoIvaRate =
    MEXICAN_TAX_CHARGES.find(c => c.name === 'iva')?.rate * 100

  useEffect(() => {
    const shouldAutoExpand =
      fuel?.provider === 'breakthrough' &&
      models[0] &&
      !hasChargeBreakdown &&
      !hasQuoted

    if (shouldAutoExpand) {
      switchToCharges(models[0])
    }
  }, [fuel?.provider, models, hasChargeBreakdown, hasQuoted, switchToCharges])

  if (hasQuoted && quotesQuery.isError && !models.length) {
    return (
      <div className="box box--help-gradient mt-4">
        <div className="icon-message">
          <Icon className="color-secondary" icon="exclamation-triangle" />
          <p className="mb-0">
            Unable to load {term}, {supportMessage}.
          </p>
        </div>
      </div>
    )
  }

  const handleSubmit = async () => {
    if (!hasQuoted) {
      try {
        await submitQuotes.mutateAsync({
          message,
          quotes: models.map(({ quoteId, ...model }) => modelToDto(model)),
        })
        setMessage('')
        formRef.current?.reset()
      } catch {
        toast.error(`Unable to add ${term}, ${supportMessage}`)
      }
      return
    }

    if (!canEdit) {
      if (!message) return
      return sendMessage({
        messageText: message,
        onSuccess: () => setMessage(''),
      })
    }

    const quoteToRetract = models.find(m => !selected.includes(m.quoteId))
    if (quoteToRetract) {
      try {
        await retractQuote.mutateAsync({
          loadId,
          quoteId: quoteToRetract.quoteId,
        })
      } catch {
        toast.error(`Unable to retract ${term}, ${supportMessage}`)
      }
    }

    const selectedModels = selected
      .map(id => models.find(m => m.quoteId === id))
      .filter((m): m is (typeof models)[number] => m !== undefined)

    const quotesToUpdate = selectedModels.filter(model =>
      hasQuoteChanged(
        model,
        quotes.find(q => q._id === model.quoteId),
      ),
    )

    if (!quotesToUpdate.length && !quoteToRetract) {
      return toast.error(`Must make a change to update ${pluralize(term, 2)}.`)
    }

    // Previously implemented form did validation, but didn't prevent submission.
    // So this is commented out to maintain consistent behavior - and left in to bring visibility to the intention.

    // const originalApiQuote = quotes.find(q => q.method === 'api')
    // const updatedApiQuote = quotesToUpdate.find(
    //   q => q.quoteId === originalApiQuote?._id,
    // )
    // if (
    //   originalApiQuote &&
    //   updatedApiQuote &&
    //   validateApiQuoteUpdate(originalApiQuote, updatedApiQuote, updateModel)
    // ) {
    //   formRef.current?.reportValidity()
    //   return
    // }

    if (quotesToUpdate.length) {
      try {
        await submitQuotes.mutateAsync({
          isUpdate: true,
          message,
          quotes: quotesToUpdate.map(modelToDto),
        })
        setContractedRateLock(true)
        setMessage('')
        formRef.current?.reset()
      } catch {
        toast.error(
          `Unable to update ${pluralize('quote', quotesToUpdate.length)}, ${supportMessage}`,
        )
      }
    } else {
      toast.error(`Must make a change to update ${pluralize(term, 2)}.`)
    }
  }

  return (
    <ValidatedForm
      onValidSubmit={handleSubmit}
      className="form-currently-quoting"
      ref={formRef}
    >
      <div>
        <QuotingDescription opportunity={opportunity} />

        <div className="mb-3">
          {hasQuoted &&
            models.map(model => (
              <QuoteFormEntry
                value={model}
                key={model.quoteId}
                disabled={isExpired || !canEdit}
                quote={quotes.find(q => q._id === model.quoteId)}
              />
            ))}

          {!hasQuoted && <QuoteFormEntry value={models[0]} />}
        </div>
        <div className="form-group">
          <TextAreaField
            className="form-control"
            disabled={isBusy || isExpired}
            value={message}
            onChange={e => setMessage(e.target.value)}
            maxLength={500}
            name="message"
            placeholder="Include an optional message here."
          />
        </div>
        <div className="flex items-center justify-between">
          <div>
            {!hasChargeBreakdown && (canEdit || !hasQuoted) && (
              <FvButton
                icon="money-check-edit-alt"
                onClick={() => switchToCharges(models[0])}
              >
                Add charges
              </FvButton>
            )}
          </div>
          <FvButton
            disabled={isBusy || isExpired}
            type="submit"
            theme="default"
            loading={isBusy}
            className="self-end"
            icon="paper-plane"
          >
            <span>
              {!hasQuoted && 'Send bid'}
              {hasQuoted &&
                (canEdit
                  ? `Update ${pluralize(term, quotes.length)}`
                  : 'Send message')}
            </span>
          </FvButton>
        </div>

        {models.some(m => m.currency === 'cad') && (
          <p className="italic text-xs mt-4">
            * CAD bids are converted to USD for shipper approval based on the
            exchange rate at the time of pickup via{' '}
            <FvLinkButton
              theme="underlined"
              href="https://www.xe.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              XE
            </FvLinkButton>
            .
          </p>
        )}
        {models.some(m => m.currency === 'mxn') && (
          <p className="mb-0 mt-3 text-xs text-fv-gray-dark">
            * MXN bids are converted to USD for shipper approval based on the
            exchange rate at the time of Pickup via{' '}
            <FvLinkButton
              theme="underlined"
              href="https://www.xe.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              XE
            </FvLinkButton>
            .
            {isMexicoIntra(locations) &&
              ` IVA (${mexicoIvaRate}%) is automatically applied for intra-Mexico shipments.`}
          </p>
        )}
      </div>
    </ValidatedForm>
  )
}
